// fork(oem): the rail's file library lists the OEM+ document corpus — real
// filenames from `public/data/oem/doc-index.json`, so a reader who opens
// Integration → Data finds the same documents there.
//
// The foundation's five generic files (Product brief.pdf, Wireframe-v2.png, …)
// are replaced; the artifacts below are left as-is, since the assistant does
// not generate artifacts in this port (report generation is Tier 3).
import type { LucideIcon } from 'lucide-react';
import { FileSpreadsheet, FileText, ShieldAlert } from 'lucide-react';
import { OEM_NOW_MS } from './oem-clock';

export type SeedFile = {
  id: string;
  name: string;
  meta: string;
  Icon: LucideIcon;
  folder: string;
  updatedAt: number;
};

export type SeedArtifactKind = 'document' | 'code';

export type SeedArtifact = {
  id: string;
  name: string;
  meta: string;
  updatedAt: number;
  kind: SeedArtifactKind;
  language?: string;
  version: number;
  previewContent: string;
  codeContent: string;
};

// fork(oem): the frozen OEM clock, not the wall clock.
const now = OEM_NOW_MS;
const hour = 60 * 60 * 1000;
const day = 24 * hour;

export const SEED_FILE_FOLDERS = ['Uploads', 'Generated', 'Shared'] as const;

export const SEED_FILES: SeedFile[] = [
  {
    id: 'file-1',
    name: 'TB-2026-031 — Hydraulic contamination and pump drive coupling failures, ML-21.docx',
    meta: 'Technical bulletin · SharePoint',
    Icon: FileText,
    folder: 'Shared',
    updatedAt: now - 4 * hour,
  },
  {
    id: 'file-2',
    name: 'SP-ML21-HYD-004 Rev B — Hydraulic pump drive coupling replacement.docx',
    meta: 'Service procedure · SharePoint',
    Icon: FileText,
    folder: 'Shared',
    updatedAt: now - 2 * day,
  },
  {
    id: 'file-3',
    name: 'WO-26-11842 — Breakdown report, L-02, Kalbarri, 03 Sep 2026.docx',
    meta: 'Incident report · Maximo',
    Icon: ShieldAlert,
    folder: 'Uploads',
    updatedAt: now - 6 * day,
  },
  {
    id: 'file-4',
    name: 'IR-2026-015 — Engine bay fire, L-18, Gawler Deep, 07 Sep 2026.docx',
    meta: 'Incident report · Maximo',
    Icon: ShieldAlert,
    folder: 'Uploads',
    updatedAt: now - 2 * day,
  },
  {
    id: 'file-5',
    name: 'css_responses_2026-09.xlsx',
    meta: 'Customer satisfaction survey · CSS',
    Icon: FileSpreadsheet,
    folder: 'Shared',
    updatedAt: now - 1 * day,
  },
  {
    id: 'file-6',
    name: 'SP-MT63-BRK-002 Rev A — Rear brake and retarder inspection.docx',
    meta: 'Service procedure · SharePoint',
    Icon: FileText,
    folder: 'Shared',
    updatedAt: now - 9 * day,
  },
];

export const SEED_ARTIFACTS: SeedArtifact[] = [
  {
    id: 'art-1',
    name: 'Status summary',
    meta: 'Document · MD',
    updatedAt: now - 2 * hour,
    kind: 'document',
    version: 2,
    previewContent:
      '# Status summary\n\n## Highlights\n\n- Chat rail parity shipped with markdown turns and tool rows.\n- SharePoint browse modal wired for attach flows.\n- Files library tabs split Files vs Artifacts.',
    codeContent:
      '# Status summary\n\n## Highlights\n\n- Chat rail parity shipped with markdown turns and tool rows.\n- SharePoint browse modal wired for attach flows.\n- Files library tabs split Files vs Artifacts.',
  },
  {
    id: 'art-2',
    name: 'Connector checklist',
    meta: 'Code · TSX',
    updatedAt: now - 1 * day,
    kind: 'code',
    language: 'tsx',
    version: 3,
    previewContent: 'Interactive checklist component with connector toggles.',
    codeContent:
      'export function ConnectorChecklist() {\n  return (\n    <ul className="space-y-2">\n      <li>GitHub — connected</li>\n      <li>Notion — pending auth</li>\n      <li>SharePoint — connected</li>\n    </ul>\n  );\n}',
  },
  {
    id: 'art-3',
    name: 'Onboarding draft',
    meta: 'Document · MD',
    updatedAt: now - 3 * day,
    kind: 'document',
    version: 1,
    previewContent:
      '# Onboarding draft\n\nWelcome to the foundation. Start from **modules/overview** and copy the Scene + transform pattern.',
    codeContent:
      '# Onboarding draft\n\nWelcome to the foundation. Start from **modules/overview** and copy the Scene + transform pattern.',
  },
];
